import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { useSnackBar } from '../use-snackbar';
import { GetRmaInventoryRequest } from 'src/redux/slices/rma-inventory/types';
import { unwrapResult } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import useUserInfo from '../user/use-user-info';
import { rmaInventoryStatusOptions } from 'src/redux/slices/rma/constants/rma-form-fields';
import { abbreviateBranchName } from 'src/utils';

const {
  selectors: rmaInventorySelector,
  actions: rmaInventoryActions
} = slices.rmaInventory;

// eslint-disable-next-line no-unused-vars
export const useRmaInventory = () => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const { userBranches } = useUserInfo();

  const filters = useAppSelector(rmaInventorySelector.selectFilters);
  const rmaInventoryItems = useAppSelector(
    rmaInventorySelector.selectRmaInverntoryItems
  );
  const isLoading = useAppSelector(
    rmaInventorySelector.selectIsInventoryLoading
  );
  const meta = useAppSelector(rmaInventorySelector.selectMeta);

  const dynamicInventoryStatusOptions = useMemo(() => {
    let arr = [...rmaInventoryStatusOptions];
    userBranches?.forEach((b) =>
      arr.push(`RMA (${abbreviateBranchName(b.branch_name)})`)
    );
    return arr;
  }, [userBranches]);

  const onChangeFilters = (payload: GetRmaInventoryRequest) => {
    dispatch(rmaInventoryActions.setFilters(payload));
  };

  const getRmaInventory = async () => {
    const res = unwrapResult(
      await dispatch(rmaInventoryActions.getRmaInventoryThunk(filters))
    );
    if (!res.success) {
      const { errors, message } = res;
      snackBar.show({
        severity: 'error',
        message: errors
          ? Object.values(errors).join(', ')
          : message ||
            'Failed to fetch rma invetorie. Please try again later or contact support'
      });
    } else {
      return res;
    }
  };

  const createCreditNote = async (id: number) => {
    const res = unwrapResult(
      await dispatch(
        rmaInventoryActions.createCreditNoteThunk({ rma_inventory_id: id })
      )
    );
    const { errors, message, success } = res.originalData;
    if (!success) {
      snackBar.show({
        severity: 'error',
        message: errors
          ? Object.values(errors).join(', ')
          : message ||
            'Failed to create credit note. Please try again later or contact support'
      });
    } else {
      snackBar.show({
        severity: 'success',
        message:
          message ?? `Successfully created credit note for rma inventory: ${id}`
      });
    }
    return res;
  };

  const createTransaction = async (id: number) => {
    const res = unwrapResult(
      await dispatch(
        rmaInventoryActions.createTransactionThunk({ rma_inventory_id: id })
      )
    );
    const { errors, message, success } = res.originalData;
    if (!success) {
      snackBar.show({
        severity: 'error',
        message: errors
          ? Object.values(errors).join(', ')
          : message ||
            'Failed to fetch rma invetorie. Please try again later or contact support'
      });
    } else {
      snackBar.show({
        severity: 'success',
        message:
          message ?? `Successfully created transaction for rma inventory: ${id}`
      });
    }
    return res;
  };

  return {
    filters,
    isLoading,
    dynamicInventoryStatusOptions,
    rmaInventoryItems,
    meta,

    onChangeFilters,
    getRmaInventory,
    createCreditNote,
    createTransaction
  };
};
